import { decorate, inject, injectable } from 'inversify';
import EventEmitter from 'wolfy87-eventemitter';
import type { IZoomController, IZoomEvents } from './zoom.interface';
import { ZOOM_MODULE_IDS } from './zoom.types';

decorate(injectable(), EventEmitter);

export class ZoomController extends EventEmitter implements IZoomController {
  zoomRange: Array<number> = [1];
  currentZoomIndex: number = 0;
  currentCustomZoom: number = null;
  isZooming: boolean = false;

  constructor(@inject(ZOOM_MODULE_IDS.ZOOM_EVENTS) public events: IZoomEvents) {
    super();
  }

  get currentZoom(): number {
    return this.currentCustomZoom || this.zoomRange[this.currentZoomIndex];
  }

  get zoomIsMaximum(): boolean {
    return this.currentZoom >= this.zoomRange[this.zoomRange.length - 1];
  }

  get zoomIsMinimum(): boolean {
    return this.currentZoom === this.zoomRange[0];
  }

  getMiddleZoom() {
    return this.zoomRange[Math.floor(this.zoomRange.length / 2)];
  }

  getMaximumZoom(): number {
    return this.zoomRange[this.zoomRange.length - 1];
  }

  getMaximumZoomIndex(): number {
    return this.zoomRange.length - 1;
  }

  getMinimumZoom(): number {
    return this.zoomRange[0];
  }

  setZoomRange(zoomRange: Array<number>): void {
    this.zoomRange = zoomRange;
    this.currentZoomIndex = 0;
    this.currentCustomZoom = null;
  }

  zoomIn({ x, y, onEnd }: { x: number; y: number; onEnd?: () => void }) {
    if (this.zoomIsMaximum) return;

    const prevScale = this.currentZoom;

    if (this.currentCustomZoom) {
      const index = this.zoomRange.findIndex((el) => this.currentCustomZoom < el);

      this.currentZoomIndex = index;
      this.currentCustomZoom = null;
    } else {
      this.currentZoomIndex += 1;
    }

    if (!this.zoomIsMinimum) {
      window.Widget.zoom({
        prevScale,
        x,
        y,
        onEnd: () => {
          window.Widget.clear();
          window.Widget.update();
          window.Widget.resetStyles();
          window.Widget.backgroundController.update();
          this.emit(this.events.UPDATE);

          if (onEnd) onEnd();
        },
      });
    }
  }

  zoomOut({ x, y }) {
    if (this.zoomIsMinimum) return;
    const prevScale = this.currentZoom;

    if (this.currentCustomZoom) {
      const index = this.zoomRange.findIndex((el) => this.currentCustomZoom > el);

      this.currentZoomIndex = index;
      this.currentCustomZoom = null;
    } else {
      this.currentZoomIndex -= 1;
    }

    if (!this.zoomIsMaximum) {
      window.Widget.zoom({
        prevScale,
        x,
        y,
        onEnd: () => {
          window.Widget.clear();
          window.Widget.update();
          window.Widget.resetStyles();
          window.Widget.backgroundController.update();
          this.emit(this.events.UPDATE);
        },
      });
    }
  }

  setZoom({ zoom, x, y }: { zoom: number; x: number; y: number }) {
    const prevScale = this.currentZoom;

    this.currentCustomZoom = zoom;

    window.Widget.zoom({
      prevScale,
      newZoom: zoom,
      x,
      y,
      onEnd: () => {
        window.Widget.clear();
        window.Widget.update();
        window.Widget.resetStylesAfterTouch();
        window.Widget.backgroundController.update();
        this.emit(this.events.UPDATE);
      },
    });
  }

  startZooming() {
    this.isZooming = true;
  }

  stopZooming() {
    this.isZooming = false;
  }
}
